import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaPen, FaRegTrashAlt } from 'react-icons/fa';
import { FaRegCircleQuestion, FaTriangleExclamation } from 'react-icons/fa6';
import dayjs, { Dayjs } from 'dayjs';

import {
  PermissionKeys,
  SpaceOccupancyPayload,
  SpaceOccupancyReadModel,
  SpaceOccupancyType,
  TenantReadModel,
  useDeleteSpacesByIdOccupanciesAndOccupancyIdMutation,
  useGetSpacesByIdOccupanciesQuery,
  usePostSpacesByIdOccupanciesMutation,
  usePutSpacesByIdOccupanciesAndOccupancyIdMutation,
} from '@client/api';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CardTitle,
  DatepickerV2,
  EmptyPlaceholder,
  InfoTooltip,
  Input,
  Modal,
  ModalFooter,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  TableRow,
  Text,
  toNumberString,
  toPercent,
} from '@client/toolkit';

import { DeleteConfirmationModalContent } from '../../../../components/delete-confirmation-modal-content/delete-confirmation-modal-content';
import { useSpacePermissions } from '../../../../hooks/useSpacePermissions/useSpacePermissions';
import { PortfolioTenantSelectionModal } from '../portfolio-tenants-card/portfolio-tenant-selection-modal.component';

/**
 * The occupancy register for a space — who is using the area, beside the counterparty of record.
 *
 * The Tenant rows are derived from the space's leases and are read-only; every other row is
 * entered here. That is why this replaces the two separate Leietakere panels: one list with a
 * type column, so "who is in here" has a single answer.
 *
 * There is deliberately no amount column. Money lives on the agreement.
 */
export interface SpaceOccupancyCardProps {
  spaceId?: string;
  buildingId?: string;
}

/** Types a user can enter. LeaseTenant is derived and never offered. */
const MANUAL_TYPES: SpaceOccupancyType[] = ['SubLease', 'Operator', 'Other'];

interface EditState {
  occupancyId: string | null; // null = new row
  stakeholderId: string | null;
  stakeholderName: string;
  /** §2 — no default on manual rows; null until the user picks one. */
  occupancyType: SpaceOccupancyType | null;
  validFrom: Dayjs | null;
  validTo: Dayjs | null;
  areaSqM: string;
  note: string;
}

const emptyEdit = (defaultAreaSqM?: number | null): EditState => ({
  occupancyId: null,
  stakeholderId: null,
  stakeholderName: '',
  // §2 — deliberately unset. Defaulting to SubLease would label every occupant framleie and
  // leave Operator and Other permanently unentered, which is the failure §1 warns about.
  occupancyType: null,
  validFrom: null,
  validTo: null,
  // §2 — the area defaults to the full space area.
  areaSqM: defaultAreaSqM != null ? String(defaultAreaSqM) : '',
  note: '',
});

const fromRow = (row: SpaceOccupancyReadModel): EditState => ({
  occupancyId: row.id ?? null,
  stakeholderId: row.stakeholderId ?? null,
  stakeholderName: row.stakeholderName ?? '',
  occupancyType: row.occupancyType,
  validFrom: row.validFrom ? dayjs(row.validFrom) : null,
  validTo: row.validTo ? dayjs(row.validTo) : null,
  areaSqM: String(row.areaSqM),
  note: row.note ?? '',
});

export function SpaceOccupancyCard({ spaceId, buildingId }: SpaceOccupancyCardProps) {
  const { t: tCommon } = useTranslation();
  const { t } = useTranslation('portfolio');

  const { hasAnyPermission } = useSpacePermissions({ id: spaceId });
  const canEdit = hasAnyPermission([PermissionKeys.Space.Update]);

  const { data: register, isLoading } = useGetSpacesByIdOccupanciesQuery(
    { id: spaceId ?? '' },
    { skip: !spaceId },
  );

  const [createOccupancy, { isLoading: isCreating }] = usePostSpacesByIdOccupanciesMutation();
  const [updateOccupancy, { isLoading: isUpdating }] = usePutSpacesByIdOccupanciesAndOccupancyIdMutation();
  const [deleteOccupancy, { isLoading: isDeleting }] = useDeleteSpacesByIdOccupanciesAndOccupancyIdMutation();

  const isSaving = isCreating || isUpdating;

  const [editing, setEditing] = useState(false);
  const [editRow, setEditRow] = useState<EditState | null>(null);
  const [selectionModalOpen, setSelectionModalOpen] = useState(false);
  const [deletingRow, setDeletingRow] = useState<SpaceOccupancyReadModel | null>(null);

  const rows = useMemo(() => register?.occupancies ?? [], [register]);
  const warnings = useMemo(() => register?.warnings ?? [], [register]);
  const spaceAreaSqM = register?.spaceAreaSqM ?? null;

  const isListEmpty = rows.length === 0 && !editing;

  const handleAdd = () => {
    setEditRow(emptyEdit(spaceAreaSqM));
    setSelectionModalOpen(true);
  };

  const handleCancel = () => {
    setEditRow(null);
    setEditing(false);
  };

  const handleStakeholderSelected = (items: TenantReadModel[]) => {
    // A company with no Aktør yields an empty id. Leaving the row unselected keeps canSave
    // false, which is a visible dead end rather than a foreign-key failure on save.
    if (items.length > 0 && items[0].id && editRow) {
      setEditRow({ ...editRow, stakeholderId: items[0].id, stakeholderName: items[0].name ?? '' });
    }
    setSelectionModalOpen(false);
  };

  const canSave =
    editRow !== null &&
    editRow.stakeholderId !== null &&
    editRow.occupancyType !== null &&
    editRow.validFrom !== null &&
    editRow.validTo !== null &&
    Number(editRow.areaSqM) > 0;

  const handleSave = async () => {
    if (!spaceId || !editRow || !canSave) return;

    const payload: SpaceOccupancyPayload = {
      stakeholderId: editRow.stakeholderId!,
      occupancyType: editRow.occupancyType!,
      validFrom: editRow.validFrom!.format('YYYY-MM-DD'),
      validTo: editRow.validTo!.format('YYYY-MM-DD'),
      areaSqM: Number(editRow.areaSqM),
      note: editRow.note.trim() || null,
    };

    try {
      if (editRow.occupancyId) {
        await updateOccupancy({ id: spaceId, occupancyId: editRow.occupancyId, body: payload }).unwrap();
      } else {
        await createOccupancy({ id: spaceId, body: payload }).unwrap();
      }
      setEditRow(null);
    } catch {
      // Swallowed on purpose: notification.saga toasts every rejected mutation globally, using
      // the server.<CODE> entries in validation.json. The row stays in edit state so the user
      // can correct it. Rethrowing here would only duplicate the toast.
    }
  };

  const handleDeleteConfirm = async () => {
    if (!spaceId || !deletingRow?.id) return;
    try {
      await deleteOccupancy({ id: spaceId, occupancyId: deletingRow.id }).unwrap();
    } catch {
      // See handleSave: the global notification saga surfaces the message.
    }
    setDeletingRow(null);
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex-1" variant="highlighted">
            {t('occupancy.title')}
          </CardTitle>
          <CardActions>
            {!editing ? (
              canEdit && (
                <Button size="sm" onClick={() => setEditing(true)}>
                  {tCommon('edit')}
                </Button>
              )
            ) : (
              <>
                <Button size="sm" color="primary-dark" onClick={handleCancel} disabled={isSaving}>
                  {tCommon('cancel')}
                </Button>
                <Button size="sm" color="primary-dark" onClick={handleAdd} disabled={isSaving || editRow !== null}>
                  {t('occupancy.addUser')}
                </Button>
                <Button size="sm" color="primary" onClick={handleSave} disabled={isSaving || !canSave}>
                  {tCommon('save')}
                </Button>
              </>
            )}
          </CardActions>
        </CardHeader>

        <CardContent variant="fill" className="justify-self-center rounded-lg w-full px-4 pb-4">
          {warnings.length > 0 && (
            <div className="mb-3 flex flex-col gap-2">
              {warnings.map((warning, idx) => (
                <div
                  key={idx}
                  className="flex items-start gap-2 rounded-md bg-yellow-500/10 px-3 py-2 text-xs text-yellow-300"
                >
                  <FaTriangleExclamation className="mt-0.5 shrink-0" />
                  <span>
                    {t(`occupancy.warning.${warning.code}`, {
                      occupied: toNumberString(warning.occupiedAreaSqM ?? 0),
                      total: toNumberString(warning.spaceAreaSqM ?? 0),
                    })}
                  </span>
                </div>
              ))}
            </div>
          )}

          <Table className="overflow-hidden w-full mb-4 rounded-md" size="xs" layout="fixed">
            {!isLoading && isListEmpty && (
              <caption className="px-4 py-4 [caption-side:bottom]">
                <EmptyPlaceholder>{t('occupancy.empty')}</EmptyPlaceholder>
              </caption>
            )}

            <TableHead>
              <TableHeadRow>
                <TableHeadCell className="w-[16%]">{t('occupancy.columns.type')}</TableHeadCell>
                <TableHeadCell className="w-[19%]">{t('occupancy.columns.company')}</TableHeadCell>
                <TableHeadCell className="w-[12%]">{t('occupancy.columns.validFrom')}</TableHeadCell>
                <TableHeadCell className="w-[12%]">{t('occupancy.columns.validTo')}</TableHeadCell>
                <TableHeadCell className="w-[20%]">{t('occupancy.columns.area')}</TableHeadCell>
                <TableHeadCell className="w-[21%]">{t('occupancy.columns.note')}</TableHeadCell>
              </TableHeadRow>
            </TableHead>

            <TableBody>
              {isLoading &&
                Array.from(Array(3).keys()).map((x) => (
                  <TableRow key={x}>
                    <TableCell loading />
                    <TableCell loading />
                    <TableCell loading />
                    <TableCell loading />
                    <TableCell loading />
                    <TableCell loading />
                  </TableRow>
                ))}

              {!isLoading &&
                rows.map((row, idx) =>
                  editing && editRow?.occupancyId != null && editRow.occupancyId === row.id ? (
                    <OccupancyEditRow
                      key={row.id ?? idx}
                      editRow={editRow}
                      setEditRow={setEditRow}
                      onOpenStakeholderSearch={() => setSelectionModalOpen(true)}
                    />
                  ) : (
                    <OccupancyViewRow
                      key={row.id ?? `derived-${idx}`}
                      row={row}
                      spaceAreaSqM={spaceAreaSqM}
                      editing={editing}
                      onEdit={() => setEditRow(fromRow(row))}
                      onDelete={() => setDeletingRow(row)}
                    />
                  ),
                )}

              {editing && editRow?.occupancyId == null && editRow !== null && (
                <OccupancyEditRow
                  editRow={editRow}
                  setEditRow={setEditRow}
                  onOpenStakeholderSearch={() => setSelectionModalOpen(true)}
                  onCancel={() => setEditRow(null)}
                />
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <PortfolioTenantSelectionModal
        open={selectionModalOpen}
        buildingId={buildingId}
        title={t('occupancy.selectCompanyTitle')}
        onClose={() => setSelectionModalOpen(false)}
        onSubmit={handleStakeholderSelected}
      />

      <Modal
        open={deletingRow !== null}
        onClose={() => setDeletingRow(null)}
        onBackdropClick={() => setDeletingRow(null)}
        closeDisabled={isDeleting}
      >
        <DeleteConfirmationModalContent name={deletingRow?.stakeholderName ?? ''} />
        <ModalFooter className="justify-center">
          <Button color="danger" onClick={handleDeleteConfirm} progressing={isDeleting}>
            {tCommon('delete')}
          </Button>
          <Button color="primary-dark" onClick={() => setDeletingRow(null)}>
            {tCommon('cancel')}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

interface OccupancyViewRowProps {
  row: SpaceOccupancyReadModel;
  spaceAreaSqM: number | null;
  editing: boolean;
  onEdit: () => void;
  onDelete: () => void;
}

function OccupancyViewRow({ row, spaceAreaSqM, editing, onEdit, onDelete }: OccupancyViewRowProps) {
  const { t } = useTranslation('portfolio');
  const { t: tType } = useTranslation('type');

  const areaUnit = tType('AreaMeasurementUnit.SquareMeters', { context: 'short' });
  // §3 — the percentage is derived for display; only the m² figure is stored.
  const areaShare =
    spaceAreaSqM != null && spaceAreaSqM > 0 ? toPercent(row.areaSqM / spaceAreaSqM) : undefined;

  return (
    <TableRow verticalAlign="middle">
      <TableCell>
        <span className="inline-flex items-center gap-1">
          {t(`occupancy.type.${row.occupancyType}`)}
          {row.isDerived && (
            <InfoTooltip
              message={t('occupancy.derivedTooltip')}
              messageClassName="block w-56 z-50"
              enterDelay="delay-100"
            >
              <FaRegCircleQuestion className="text-gray-400" aria-label={t('occupancy.derivedTooltip')} />
            </InfoTooltip>
          )}
        </span>
      </TableCell>
      <TableCell>
        <Text isEmpty={!row.stakeholderName}>
          <strong>{row.stakeholderName ?? '\u2013'}</strong>
        </Text>
      </TableCell>
      <TableCell>{row.validFrom ? dayjs(row.validFrom).format('DD.MM.YYYY') : '\u2013'}</TableCell>
      <TableCell>{row.validTo ? dayjs(row.validTo).format('DD.MM.YYYY') : '\u2013'}</TableCell>
      <TableCell>
        <Text as="div" className="whitespace-nowrap">
          {toNumberString(row.areaSqM, '\u2013', { unit: areaUnit })}
        </Text>
        {areaShare && (
          <Text as="div" className="text-xs opacity-60 whitespace-nowrap">
            {t('occupancy.areaShare', { percent: areaShare })}
          </Text>
        )}
      </TableCell>
      <TableCell className="flex items-center justify-between gap-2">
        <span className="truncate">{row.note ?? '\u2013'}</span>
        {/* Derived rows are read-only: the counterparty is changed on the agreement. */}
        {editing && !row.isDerived && (
          <span className="flex shrink-0 items-center">
            <Button className="ml-1 text-brand-primary bg-surface-elevated" size="xs" color="custom" onClick={onEdit}>
              <FaPen />
            </Button>
            <Button className="ml-1 text-brand-primary bg-surface-elevated" size="xs" color="custom" onClick={onDelete}>
              <FaRegTrashAlt />
            </Button>
          </span>
        )}
      </TableCell>
    </TableRow>
  );
}

interface OccupancyEditRowProps {
  editRow: EditState;
  setEditRow: (state: EditState) => void;
  onOpenStakeholderSearch: () => void;
  onCancel?: () => void;
}

function OccupancyEditRow({ editRow, setEditRow, onOpenStakeholderSearch, onCancel }: OccupancyEditRowProps) {
  const { t } = useTranslation('portfolio');

  return (
    <TableRow verticalAlign="middle">
      <TableCell>
        <Select
          size="medium"
          className="w-full"
          value={editRow.occupancyType ?? ''}
          onChange={(e) =>
            setEditRow({
              ...editRow,
              occupancyType: e.target.value === '' ? null : (e.target.value as SpaceOccupancyType),
            })
          }
        >
          <option value="">{t('occupancy.selectType')}</option>
          {MANUAL_TYPES.map((type) => (
            <option key={type} value={type}>
              {t(`occupancy.type.${type}`)}
            </option>
          ))}
        </Select>
      </TableCell>
      <TableCell>
        <Button size="sm" color="primary-dark" className="w-full text-left truncate" onClick={onOpenStakeholderSearch}>
          {editRow.stakeholderName || t('occupancy.selectCompany')}
        </Button>
      </TableCell>
      <TableCell>
        <DatepickerV2
          value={editRow.validFrom ?? undefined}
          onChange={(date) => setEditRow({ ...editRow, validFrom: date ?? null })}
          className="w-full !min-w-0"
        />
      </TableCell>
      <TableCell>
        <DatepickerV2
          value={editRow.validTo ?? undefined}
          onChange={(date) => setEditRow({ ...editRow, validTo: date ?? null })}
          className="w-full !min-w-0"
        />
      </TableCell>
      <TableCell>
        <Input
          type="number"
          size="medium"
          value={editRow.areaSqM}
          onChange={(e) => setEditRow({ ...editRow, areaSqM: e.target.value })}
          placeholder="0"
        />
      </TableCell>
      <TableCell className="flex items-center gap-2">
        <Input
          size="medium"
          value={editRow.note}
          onChange={(e) => setEditRow({ ...editRow, note: e.target.value })}
        />
        {onCancel && (
          <Button className="text-brand-primary bg-surface-elevated" size="xs" color="custom" onClick={onCancel}>
            <FaRegTrashAlt />
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
}
